import React, { useEffect, useState } from 'react';
import { Button, Grid2, IconButton, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { deleteProductImage } from '../../../services/api/ProductAPI';
import type { FormData } from '../../../types';

interface ProductImagesProps {
	formData: FormData;
	setFormData: React.Dispatch<React.SetStateAction<FormData>>;
	disabled?: boolean;
}

const ProductImages: React.FC<ProductImagesProps> = ({ formData, setFormData, disabled }) => {
	const [imagePreviews, setImagePreviews] = useState<string[]>([]);

	const [isDeleting, setIsDeleting] = useState(false);
	useEffect(() => {
		if (formData.productImage?.length > 0) {
			const previews = formData.productImage.map((img) =>
				typeof img === 'string' ? img : URL.createObjectURL(img)
			);
			setImagePreviews(previews);
			if (
				!formData.imageAltTags ||
				formData.imageAltTags.length !== formData.productImage.length
			) {
				setFormData((prev) => ({
					...prev,
					imageAltTags: formData.productImage.map(
						(_, idx) => formData.imageAltTags?.[idx] || ''
					),
				}));
			}
		} else {
			setImagePreviews([]);
		}
	}, [formData.productImage]);

	useEffect(() => {
		imagePreviews.forEach((src, idx) => {
			if (formData.productImage[idx] instanceof File) {
				URL.revokeObjectURL(src);
			}
		});
	}, []);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files ? Array.from(e.target.files) : [];
		if (files.length === 0) return;

		const newPreviews = files.map((file) => URL.createObjectURL(file));

		setFormData((prev) => ({
			...prev,
			productImage: [...prev.productImage, ...files],
			imageAltTags: [...(prev.imageAltTags || []), ...files.map(() => '')],
		}));

		setImagePreviews((prev) => [...prev, ...newPreviews]);
	};

	const handleAltTagChange = (index: number, value: string) => {
		const updatedAltTags = [...(formData.imageAltTags || [])];
		updatedAltTags[index] = value;
		setFormData((prev) => ({ ...prev, imageAltTags: updatedAltTags }));
	};

	const handleRemoveImage = async (index: number, imageSrc: string) => {
		console.log(imageSrc, 'imageSrc');
		try {
			setIsDeleting(true);
			const imageToRemove = formData.productImage[index];
			if (typeof imageToRemove === 'string' && formData.productId) {
				const response = await deleteProductImage(formData.productId, {
					imageUrl: imageToRemove,
				});
				if (response.success) {
					toast.success('Image deleted successfully');
				} else {
					toast.error(response.data.errors || 'Server error');
					return;
				}
			}

			setFormData((prev) => ({
				...prev,
				productImage: prev.productImage.filter((_, i) => i !== index),
				imageAltTags: (prev.imageAltTags || []).filter((_: any, i: any) => i !== index),
			}));

			setImagePreviews((prev) => {
				const updated = [...prev];
				if (formData.productImage[index] instanceof File) {
					URL.revokeObjectURL(updated[index]);
				}
				updated.splice(index, 1);
				return updated;
			});
		} catch (err) {
			console.error(err);
			toast.error('Failed to delete image');
		} finally {
			setIsDeleting(false);
		}
	};

	const getImageUrl = (path: string) => {
		if (!path) return '/assets/image-not-found.webp';
		if (path.startsWith('http')) return path;
		return `${process.env.REACT_APP_BASE_IMAGE_URL}${path}`;
	};

	return (
		<Grid2 container spacing={2}>
			<Grid2 size={{ xs: 12 }}>
				<input
					type="file"
					id="productImages"
					accept="image/png, image/jpeg"
					multiple
					style={{ display: 'none' }}
					onChange={handleFileChange}
					disabled={disabled}
				/>
				<label htmlFor="productImages">
					<Button
						variant="contained"
						component="span"
						color="primary"
						disabled={disabled}
					>
						Upload Images
					</Button>
				</label>
			</Grid2>

			{imagePreviews?.map((src, index) => (
				<Grid2 key={index} size={{ xs: 12, sm: 6, md: 4 }}>
					<div style={{ position: 'relative', marginBottom: '8px' }}>
						<img
							src={
								formData.productImage[index] instanceof File
									? src
									: getImageUrl(src)
							}
							alt={formData.imageAltTags?.[index] || `Image ${index + 1}`}
							style={{ width: '100%', height: '150px', objectFit: 'cover' }}
						/>
						<IconButton
							onClick={() => handleRemoveImage(index, src)}
							style={{ position: 'absolute', top: 4, right: 4 }}
							disabled={isDeleting || disabled}
						>
							<DeleteIcon />
						</IconButton>
					</div>

					<TextField
						size="small"
						label="Image Alt Text"
						fullWidth
						value={formData.imageAltTags?.[index] || ''}
						onChange={(e) => handleAltTagChange(index, e.target.value)}
						placeholder="Enter image alt tag"
						disabled={disabled}
					/>
				</Grid2>
			))}
		</Grid2>
	);
};

export default ProductImages;
